import {ReactElement, useContext, useEffect} from "react";
import {WebsocketEvent} from "@devour/client";
import {WebsocketProviderContext} from "@/components/WebsocketProvider";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";

/**
 * Hook to initialize a webhook listener on the React app.
 * @param event
 * @param listenerCallback The function that will be called when the backend emits the listened event. Pass in the typescript interface as a generic prop.
 * @param authRequired Only connect listener if user is logged in
 * @example
 * useWebsocket<LevelUpWebhookResponse>(WebsocketEvent.LEVEL_UP, handleNewLevel, true);
 * function handleNewLevel(data: LevelUpWebhookResponse) {
 *   alert(`Your new level is ${data.userLevel}`);
 * }
 */
function useWebsocket<D = any>(event: WebsocketEvent, listenerCallback: (data: D) => void, authRequired?: boolean): ReactElement {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {socket, socketConnection} = useContext(WebsocketProviderContext);

    useEffect(() => {
        let isEventAdded: boolean = false;
        const authCheck = Boolean(!authRequired || fullToken);
        if (socketConnection && authCheck && socket) {
            socket.on(event, listenerCallback);
            isEventAdded = true;
        }

        return () => {
            if (isEventAdded && socket) {
                socket.off(event, listenerCallback);
            }
        };
    }, [socket, socketConnection, fullToken?.id, event, listenerCallback, authRequired]);

    return null;
}

export default useWebsocket;
